import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {formatPurchaseAssetLabels} from '../utils';
import {applyBuyerFilter} from '../utils/buyer-filter.utils';

import type {BuyerFilterState} from '../config/buyer-filter.config';
import type {BuyerTableRow} from '../types/buyers.types';

export function useFilteredBuyers(
  rows: BuyerTableRow[],
  searchText: string,
  filter?: BuyerFilterState
): BuyerTableRow[] {
  const {t} = useTranslation('trustAccounts');

  // Memoized per row-set — descriptor-driven labels aren't free to rebuild on every keystroke.
  const searchable = useMemo(
    () =>
      rows.map(row => ({
        row,
        haystack: [
          ...row.buyers.map(b => b.fullName),
          formatPurchaseAssetLabels(row.purchase.assets, t),
          ...row.purchase.assets.map(asset => asset.displayCode)
        ]
          .join(' ')
          .toLowerCase()
      })),
    [rows, t]
  );

  return useMemo(() => {
    let result = rows;

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      const matchingPurchaseIds = new Set(
        searchable.filter(s => s.haystack.includes(query)).map(s => s.row.purchase.id)
      );
      result = result.filter(row => matchingPurchaseIds.has(row.purchase.id));
    }

    if (filter) {
      result = applyBuyerFilter(result, filter);
    }

    return result;
  }, [rows, searchable, searchText, filter]);
}
